import { supabase } from './supabaseClient';

/**
 * Fetch all deliveries with optional filters
 * Supports layered filters: orderType + status applied together (AND logic)
 */
export async function fetchDeliveries({ orderType, status, search } = {}) {
  let query = supabase
    .from('coco_republic_orders')
    .select(`
      *,
      action_notes!delivery_id (
        id,
        note_text,
        created_by,
        created_at
      ),
      status_history!order_id (
        id,
        old_status,
        new_status,
        changed_by,
        changed_at
      )
    `)
    .order('created_at', { ascending: false });

  // Apply order type filter (layered - works alongside status filter)
  if (orderType && orderType !== 'all') {
    query = query.eq('order_type', orderType);
  }

  // Apply status filter (layered - works alongside order type filter)
  if (status === 'actioned') {
    query = query.eq('is_actioned', true);
  } else if (status && ['pending', 'failed', 'completed'].includes(status)) {
    query = query.eq('status', status).eq('is_actioned', false);
  }

  // Search filter
  if (search) {
    query = query.or(
      `name.ilike.%${search}%,customer_reference.ilike.%${search}%,customer_no.ilike.%${search}%`
    );
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching deliveries:', error);
    throw error;
  }

  // Transform snake_case to camelCase for frontend
  const transformedData = data.map(transformDelivery);

  // Group orders by customer_reference
  return groupByCustomerReference(transformedData);
}

/**
 * Group deliveries by customer_reference
 * Orders with the same customer_reference are combined into a single grouped order
 */
function groupByCustomerReference(deliveries) {
  const groups = new Map();

  for (const delivery of deliveries) {
    const key = delivery.customerReference || delivery.id; // Use ID as fallback if no customerReference

    if (groups.has(key)) {
      const group = groups.get(key);
      group.lineItems.push(delivery);
      // Merge action notes from all items
      group.actionNotes = [
        ...group.actionNotes,
        ...delivery.actionNotes,
      ];
      // Merge status history from all items
      group.statusHistory = [
        ...group.statusHistory,
        ...delivery.statusHistory,
      ];
      // Update group status - if any item is not actioned, group is not actioned
      if (!delivery.isActioned) {
        group.isActioned = false;
      }
      // Use the most recent date
      if (delivery.createdAt > group.createdAt) {
        group.createdAt = delivery.createdAt;
      }
    } else {
      // Create a new group with the first item's details as the header
      groups.set(key, {
        id: delivery.id, // Use first item's ID as group ID
        customerReference: delivery.customerReference,
        customerNo: delivery.customerNo,
        name: delivery.name,
        suburb: delivery.suburb,
        state: delivery.state,
        postCode: delivery.postCode,
        street: delivery.street,
        streetNo: delivery.streetNo,
        unitNo: delivery.unitNo,
        mobile: delivery.mobile,
        email: delivery.email,
        directionNotes: delivery.directionNotes,
        docType: delivery.docType,
        orderType: delivery.orderType,
        status: delivery.status,
        isActioned: delivery.isActioned,
        actionedBy: delivery.actionedBy,
        createdAt: delivery.createdAt,
        updatedAt: delivery.updatedAt,
        reqDate: delivery.reqDate,
        actionNotes: [...delivery.actionNotes],
        statusHistory: [...delivery.statusHistory],
        lineItems: [delivery],
      });
    }
  }

  // Sort action notes by timestamp (most recent first) and dedupe
  for (const group of groups.values()) {
    const uniqueNotes = new Map();
    for (const note of group.actionNotes) {
      if (!uniqueNotes.has(note.id)) {
        uniqueNotes.set(note.id, note);
      }
    }
    group.actionNotes = Array.from(uniqueNotes.values())
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    // Dedupe status history at order level: one entry per status transition per time window
    const uniqueHistory = new Map();
    for (const entry of group.statusHistory) {
      // Key by old_status + new_status + timestamp (rounded to nearest minute) for order-level dedup
      const timeKey = entry.changedAt ? entry.changedAt.slice(0, 16) : '';
      const dedupeKey = `${entry.oldStatus || ''}->${entry.newStatus}@${timeKey}`;
      if (!uniqueHistory.has(dedupeKey)) {
        uniqueHistory.set(dedupeKey, entry);
      }
    }
    group.statusHistory = Array.from(uniqueHistory.values())
      .sort((a, b) => new Date(a.changedAt) - new Date(b.changedAt));
  }

  return Array.from(groups.values());
}

/**
 * Get a single delivery by ID
 */
export async function getDeliveryById(id) {
  const { data, error } = await supabase
    .from('coco_republic_orders')
    .select(`
      *,
      action_notes!delivery_id (
        id,
        note_text,
        created_by,
        created_at
      ),
      status_history!order_id (
        id,
        old_status,
        new_status,
        changed_by,
        changed_at
      )
    `)
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching delivery:', error);
    throw error;
  }

  return transformDelivery(data);
}

/**
 * Mark a delivery (or grouped order) as actioned
 * If lineItemIds are provided, marks all items in the group
 */
export async function markDeliveryAsActioned(id, userId, lineItemIds = null) {
  const idsToUpdate = lineItemIds || [id];

  const { data, error } = await supabase
    .from('coco_republic_orders')
    .update({
      is_actioned: true,
      actioned_by: userId,
      updated_at: new Date().toISOString(),
    })
    .in('id', idsToUpdate)
    .select();

  if (error) {
    console.error('Error marking delivery as actioned:', error);
    throw error;
  }

  // Add an action note to all items
  for (const itemId of idsToUpdate) {
    await addActionNote(itemId, 'Marked as actioned', userId);
  }

  return data.map(transformDelivery);
}

/**
 * Add an action note to a delivery
 */
export async function addActionNote(deliveryId, noteText, createdBy = 'System') {
  const { data, error } = await supabase
    .from('action_notes')
    .insert({
      delivery_id: deliveryId,
      note_text: noteText,
      created_by: createdBy,
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding action note:', error);
    throw error;
  }

  // Update the delivery's updated_at timestamp
  await supabase
    .from('coco_republic_orders')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', deliveryId);

  return {
    id: data.id,
    text: data.note_text,
    createdBy: data.created_by,
    timestamp: data.created_at,
  };
}

/**
 * Get status counts for dashboard statistics
 * Counts grouped orders (by customer_reference), not individual line items
 */
export async function getStatusCounts() {
  const { data, error } = await supabase
    .from('coco_republic_orders')
    .select('id, customer_reference, order_type, status, is_actioned');

  if (error) {
    console.error('Error fetching status counts:', error);
    throw error;
  }

  // Group by customer_reference so we count orders, not line items
  const groups = new Map();
  for (const row of data) {
    const key = row.customer_reference || row.id;
    if (!groups.has(key)) {
      groups.set(key, row);
    } else {
      // If any item in the group is not actioned, the group is not actioned
      const existing = groups.get(key);
      if (!row.is_actioned) {
        existing.is_actioned = false;
      }
    }
  }

  const orders = Array.from(groups.values());

  const counts = {
    // Total counts
    all: orders.length,
    actioned: orders.filter((d) => d.is_actioned).length,
    // Order type counts
    standard: orders.filter((d) => d.order_type === 'standard').length,
    return: orders.filter((d) => d.order_type === 'return').length,
    exchange: orders.filter((d) => d.order_type === 'exchange').length,
    // Status counts - exclude actioned orders so counts match filtered results
    pending: orders.filter((d) => d.status === 'pending' && !d.is_actioned).length,
    failed: orders.filter((d) => d.status === 'failed' && !d.is_actioned).length,
    completed: orders.filter((d) => d.status === 'completed' && !d.is_actioned).length,
  };

  return counts;
}

/**
 * Transform database record to frontend format (snake_case to camelCase)
 */
function transformDelivery(record) {
  if (!record) return null;

  return {
    id: record.id,
    name: record.name,
    customerNo: record.customer_no,
    customerReference: record.customer_reference,
    suburb: record.suburb,
    state: record.state,
    postCode: record.post_code,
    street: record.street,
    streetNo: record.street_no,
    unitNo: record.unit_no,
    mobile: record.mobile,
    email: record.email,
    docType: record.doc_type,
    sku: record.sku,
    facility: record.facility,
    qty: record.qty,
    reqDate: record.req_date_h,
    deliveryType: record.delivery_type,
    installation: record.installation,
    serialNo: record.serial_no,
    returnReason: record.return_reason,
    deliveryNotes: record.delivery_notes,
    directionNotes: record.direction_notes,
    orderType: record.order_type,
    status: record.status,
    isActioned: record.is_actioned,
    actionedBy: record.actioned_by,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
    actionNotes: (record.action_notes || []).map((note) => ({
      id: note.id,
      text: note.note_text,
      createdBy: note.created_by,
      timestamp: note.created_at,
    })),
    statusHistory: (record.status_history || []).map((entry) => ({
      id: entry.id,
      oldStatus: entry.old_status,
      newStatus: entry.new_status,
      changedBy: entry.changed_by,
      changedAt: entry.changed_at,
    })),
  };
}

/**
 * Transform frontend format to database format (camelCase to snake_case)
 */
export function transformToDatabase(delivery) {
  return {
    name: delivery.name,
    customer_no: delivery.customerNo,
    customer_reference: delivery.customerReference,
    suburb: delivery.suburb,
    state: delivery.state,
    post_code: delivery.postCode,
    street: delivery.street,
    street_no: delivery.streetNo,
    unit_no: delivery.unitNo,
    mobile: delivery.mobile,
    email: delivery.email,
    doc_type: delivery.docType,
    sku: delivery.sku,
    facility: delivery.facility,
    qty: delivery.qty,
    req_date_h: delivery.reqDate,
    delivery_type: delivery.deliveryType,
    installation: delivery.installation,
    serial_no: delivery.serialNo,
    return_reason: delivery.returnReason,
    delivery_notes: delivery.deliveryNotes,
    direction_notes: delivery.directionNotes,
    order_type: delivery.orderType,
    status: delivery.status,
    is_actioned: delivery.isActioned,
    actioned_by: delivery.actionedBy,
  };
}
